import { delay } from '@/utils/delay';
import * as mocks from '@/mocks';

async function withDelay(data, ms = 350) {
  await delay(ms);
  return structuredClone(data);
}

/** Session-mutable stores so import/create flows sync across pages. */
const store = {
  invoices: structuredClone(mocks.invoices),
  accountsReceivable: structuredClone(mocks.accountsReceivable),
  accountsPayable: structuredClone(mocks.accountsPayable),
};

export const mockApi = {
  getDashboard: () =>
    withDelay({
      summary: mocks.dashboardSummary,
      cashFlow: mocks.cashFlowSeries,
      revenueExpense: mocks.revenueExpenseSeries,
      indicators: mocks.executiveIndicators,
      upcomingPayables: mocks.upcomingPayables,
      upcomingReceivables: mocks.upcomingReceivables,
      recentMovements: mocks.recentMovements,
      cashFlowDetails: mocks.cashFlowDetails,
    }),

  getClients: () => withDelay(mocks.clients),
  getClient: async (id) => {
    await delay(300);
    const client = mocks.clients.find((item) => item.id === id);
    if (!client) return null;
    return {
      ...structuredClone(client),
      receivables: structuredClone(store.accountsReceivable.filter((item) => item.clientId === id)),
      invoices: structuredClone(store.invoices.filter((item) => item.clientId === id)),
      timeline: [
        { date: '04/09/2026', title: 'Pagamento recebido', amount: 1500 },
        { date: '01/09/2026', title: 'NFe autorizada', amount: 5000 },
        { date: '01/09/2026', title: 'NFS-e emitida', amount: 2500 },
        { date: '20/08/2026', title: 'Conta a receber criada', amount: 2500 },
        { date: '10/08/2026', title: 'Suporte mensal faturado', amount: 1500 },
      ],
    };
  },

  getSuppliers: () => withDelay(mocks.suppliers),
  getSupplier: async (id) => {
    await delay(300);
    const supplier = mocks.suppliers.find((item) => item.id === id);
    if (!supplier) return null;
    return {
      ...structuredClone(supplier),
      payables: structuredClone(store.accountsPayable.filter((item) => item.supplierId === id)),
      invoices: structuredClone(store.invoices.filter((item) => item.supplierId === id)),
      documents: structuredClone(
        mocks.documents.filter((item) =>
          item.relatedTo.toLowerCase().includes(supplier.fantasyName.toLowerCase()),
        ),
      ),
      timeline: [
        { date: '01/09/2026', title: 'Conta a pagar gerada', amount: supplier.openAmount },
        { date: '28/08/2026', title: 'NFe importada', amount: 3300 },
        { date: '01/08/2026', title: 'Pagamento realizado', amount: 450 },
        { date: '01/07/2026', title: 'Pagamento realizado', amount: 450 },
      ],
    };
  },

  getProducts: () => withDelay(mocks.products),
  getContracts: () => withDelay(mocks.contracts),
  getAdjustments: () => withDelay(mocks.adjustments),
  getIndices: () => withDelay(mocks.indices),
  getAccountsPayable: () => withDelay(store.accountsPayable),
  getAccountsReceivable: () => withDelay(store.accountsReceivable),
  getTransactions: () => withDelay(mocks.transactions),
  getBankAccounts: () => withDelay(mocks.bankAccounts),
  getInvoices: () => withDelay(store.invoices),
  getBillings: () => withDelay(mocks.billings),
  getDocuments: () => withDelay(mocks.documents),
  getPasswords: () => withDelay(mocks.passwords),
  getReports: () => withDelay({ catalog: mocks.reportCatalog, dre: mocks.dreData }),
  getUsers: () => withDelay(mocks.users),
  getCategories: () => withDelay(mocks.categories),
  getProductTypes: () => withDelay(mocks.productTypes),
  getCostCenters: () => withDelay(mocks.costCenters),
  getNotifications: () => withDelay(mocks.notifications),
  getOrganization: () => withDelay(mocks.organization),
  getPermissions: () =>
    withDelay({
      modules: mocks.permissionModules,
      actions: mocks.permissionActions,
      roles: mocks.rolePermissions,
    }),
  search: async (query) => {
    await delay(200);
    return mocks.searchAll(query);
  },

  createInvoice: async (invoice) => {
    await delay(200);
    store.invoices = [invoice, ...store.invoices];
    return structuredClone(invoice);
  },

  createAccountsReceivable: async (items) => {
    await delay(200);
    const list = Array.isArray(items) ? items : [items];
    store.accountsReceivable = [...list, ...store.accountsReceivable];
    return structuredClone(list);
  },

  createAccountsPayable: async (items) => {
    await delay(200);
    const list = Array.isArray(items) ? items : [items];
    store.accountsPayable = [...list, ...store.accountsPayable];
    return structuredClone(list);
  },

  replaceAccountsReceivable: async (rows) => {
    await delay(150);
    store.accountsReceivable = structuredClone(rows);
    return structuredClone(store.accountsReceivable);
  },

  replaceAccountsPayable: async (rows) => {
    await delay(150);
    store.accountsPayable = structuredClone(rows);
    return structuredClone(store.accountsPayable);
  },
};
